// @flow

import HCaptcha from '@hcaptcha/react-hcaptcha';
import { BasePath } from 'config/paths';
import React, { useState, useEffect, useCallback, useRef, type Node, useMemo } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled, { type StyledComponent } from 'styled-components';

import loaderTokenImage from '../../../assets/images/loader-token.gif';
import config from '../../../config/app.config.js';
import { getLang, changeLanguageL10n, __ } from '../../../i18n/l10n';
import { useScroll, DocEltContextProvider } from '../../../modules/shared';
import {
  WS_GET_RECAPTCHA,
  WS_INIT_REGISTRATION,
  WS_READY,
  SET_LANGUAGE,
  STEP_UPDATE,
  SAVE_JWT,
  WS_CLEAR_PENDING,
  REFRESH_TOKEN,
  GLOBAL_LOADING,
  WS_GET_USER_REGISTRATION_REQUEST,
  REDIRECT_USER,
  WS_CLEAR_RESPONSES,
  WS_DISPLAY_ERROR_LAYER,
  SET_SESSION_ID,
  GET_SESSION_ID,
  SET_CAPTCHA,
} from '../../../store/actions';
import { hasScannedIdSelector } from '../../../store/selectors/common';
import type { RootState } from '../../../store/types';
import { media, timings } from '../../../styles/GlobalStyle';
import { API } from '../../../utils/APIUtils';
import { getParameterByName, getCookie } from '../../../utils/CommonUtils';
import { pageWithoutHeader } from '../../../utils/LayoutUtils';
import { stepToRedirectTo } from '../../../utils/RoutingUtils';

type MainRawProps = {
  className: string,
  children: Node,
};

const MainStyle: StyledComponent<{}, {}, HTMLDivElement> = styled.div`
  position: relative;
  top: 0;
  transition: top ${timings.shadow};

  ${media.greaterThan('desktop')`
    top: ${({ $scrolled, $noHeader }) => ($noHeader ? 0 : $scrolled ? 58 : 88)}px;
  `}
`;

export const Main = ({ className, children }: MainRawProps) => {
  const dispatch = useDispatch();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const common = useSelector((state: RootState) => state.common);
  const websocket = useSelector((state: RootState) => state.websocket);
  const userRegistration = useSelector((state: RootState) => state.userRegistration);
  const franceConnect = useSelector((state: RootState) => state.franceConnect);
  const language = useSelector((state: RootState) => state.i18n.language);
  const deposit = useSelector((state: RootState) => state.deposit);
  const session = useSelector((state: RootState) => state.session);
  const userHasScannedId = useSelector(hasScannedIdSelector);
  const history = useHistory();
  const [getParamsInitialized, setGetParamsInitialized] = useState(false);
  const timeoutInterval = useRef();
  const captchaRef = useRef();
  const [captchaToken, setCaptchaToken] = useState();
  const [captchaLoaded, setCaptchaLoaded] = useState(false);

  const { y } = useScroll();
  const scrolled = y > 0;

  const match = history.location.pathname.match(/(?:step)[0-9]+/);
  let currentStep: number = Number(match ? match[0].substring(4, 6) : 0);
  if (history.location.pathname.startsWith('/landing')) currentStep = 0;
  if (history.location.pathname === '/deposit') currentStep = 11;
  if (history.location.pathname === '/limit') currentStep = 12;
  if (history.location.pathname === '/paiement') currentStep = 13;
  if (history.location.pathname === '/pronos') currentStep = 14;

  const noRedirectPage = useMemo(() => ['/redirect/store', '/pronos'], []);
  const shouldNotRedirect = useMemo(
    () => noRedirectPage.some(page => history.location.pathname === page),
    [noRedirectPage],
  );

  const noHeader = useMemo(
    () => pageWithoutHeader.some(page => history.location.pathname === page),
    [pageWithoutHeader],
  );

  const firstStepPath = useMemo(() => {
    if (history.location.pathname === '/pronos') {
      return '/pronos';
    }
    return '/step1';
  }, []);

  useEffect(() => {
    if (!config.shouldUseHcaptcha) {
      // Initial get google recaptcha (needed to use 'executeRecaptcha' into epics)
      dispatch({
        type: WS_GET_RECAPTCHA,
        payload: executeRecaptcha,
      });
    }
  }, [dispatch, executeRecaptcha]);

  // Force Step 1 if no cookie
  useEffect(() => {
    if (getParamsInitialized) {
      const jwt = getCookie(config.nameCookieJWT);
      if (!jwt && !config.isForce) {
        // No redirection
        if (
          shouldNotRedirect
          || history.location.pathname.startsWith('/landing')
          || (config.hosted && config.landingName && !window.location.pathname.startsWith(BasePath))
        ) {
          return;
        }

        history.replace(firstStepPath);
        dispatch({
          type: GLOBAL_LOADING,
          payload: false,
        });
      }
      if (jwt) {
        dispatch({
          type: GLOBAL_LOADING,
          payload: {
            spine: 'token',
            fallback: loaderTokenImage,
            title: __('Hold on'),
            text: __('We\'re fetching your data...'),
          },
        });
      }
    }
  }, [dispatch, getParamsInitialized, history, shouldNotRedirect]);

  // Update step based on location
  useEffect(() => {
    dispatch({
      type: STEP_UPDATE,
      payload: currentStep,
    });
  }, [history.location, currentStep, dispatch]);

  const checkToken = useCallback(() => {
    if (!common.jwtExpirationTime) {
      return;
    }
    const expirationDate = new Date(common.jwtExpirationTime * 1000);
    const nowDate = new Date();
    const diffMs = expirationDate.getTime() - nowDate.getTime();
    const diffDays = Math.floor(diffMs / 86400000);
    const diffHrs = Math.floor((diffMs % 86400000) / 3600000);
    const diffMins = Math.round(((diffMs % 86400000) % 3600000) / 60000);
    if (!common.jwt || diffDays >= 1 || diffHrs >= 1) {
      return;
    }
    if (diffMins <= config.refreshToken.beforeCallRefreshTokenMin) {
      dispatch({
        type: REFRESH_TOKEN,
        payload: common.jwt,
      });
    }
  }, [common.jwt, common.jwtExpirationTime, dispatch]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      checkToken();
    }, config.refreshToken.intervalCheckTokenSec * 1000);
    return () => {
      window.clearInterval(timer);
    };
  }, [checkToken]);

  useEffect(() => {
    if (common.jwtExpirationTime === -1 && currentStep !== 1 && !config.isForce) {
      history.replace(firstStepPath);
      dispatch({
        type: GLOBAL_LOADING,
        payload: false,
      });
    }
  }, [common.jwtExpirationTime, currentStep, dispatch, history]);

  // Redirect to correct step based on data from getUser
  const redirectUser = useCallback(() => {
    dispatch({
      type: GLOBAL_LOADING,
      payload: false,
    });
    if (userRegistration.user) {
      const { user } = userRegistration;
      const step = stepToRedirectTo(user, franceConnect, userHasScannedId);
      // No redirection for ROUTES
      if (history.location.pathname === '/routes' && config.isDebug) {
        return;
      }
      // No redirection for DEBUG MODE or /check-deposit page
      if (config.isForce || history.location.pathname.search('/check-deposit/') !== -1) return;

      // No redirection
      if (shouldNotRedirect) {
        return;
      }

      step && history.replace(step);
    }
  }, [dispatch, history, userRegistration, shouldNotRedirect]);

  // Catch user redirection request
  useEffect(() => {
    if (!common.redirectUser) return;
    redirectUser();
    dispatch({ type: REDIRECT_USER, payload: false });
  }, [common.redirectUser, dispatch, redirectUser]);

  // Call InitUser
  useEffect(() => {
    if (websocket.layerError) return;
    /*
    A. If JWT :
      1. InitUser
      2. RefreshToken
      3. GetUser
      Ready!
    B No JWT :
      1. InitUser
      Ready!
    */
    if (userRegistration.initializing === false && userRegistration.initialized === false) {
      dispatch({
        type: WS_INIT_REGISTRATION,
      });
    } else if (
      userRegistration.initializing === false
      && userRegistration.initialized
      && websocket.ready === null
      && !common.jwt
      && !config.isMockUp
    ) {
      const jwt = getCookie(config.nameCookieJWT);
      if (jwt) {
        dispatch({
          type: REFRESH_TOKEN,
          payload: jwt,
        });
        dispatch({ type: SET_SESSION_ID });
      } else {
        // Note : you can remove this dispatch to call GetUser just after CreateUser
        dispatch({
          type: WS_READY,
          payload: true,
        });
      }
    } else if (
      userRegistration.initializing === false
      && userRegistration.initialized
      && websocket.ready === null
      && common.jwt !== null
      && common.jwtExpirationTime !== null
      && !userRegistration.getUserRegistration.success
      && !userRegistration.getUserRegistration.loading
      && session.setSessionId.success
      && !config.isMockUp
    ) {
      // Error refresh token
      if (!common.jwtExpirationTime) {
        dispatch({
          type: GLOBAL_LOADING,
          payload: false,
        });
        dispatch({
          type: WS_READY,
          payload: true,
        });
        // Delete cookie
        document.cookie = `${config.nameCookieJWT}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        return;
      }
      dispatch({
        type: WS_GET_USER_REGISTRATION_REQUEST,
        payload: {
          method: API.GetUserRegistration,
          params: {
            jwt: common.jwt,
            checkCoherence: true,
          },
        },
      });
    } else if (
      userRegistration.getUserRegistration.success
      && websocket.ready === null
      && session.setSessionId.success
    ) {
      dispatch({
        type: WS_READY,
        payload: true,
      });
      dispatch({
        type: GLOBAL_LOADING,
        payload: false,
      });
      dispatch({ type: WS_CLEAR_PENDING });
      redirectUser();
    } else if (userRegistration.getUserRegistration.error) {
      if (!config.isForce) {
        history.replace(firstStepPath);
      }

      dispatch({
        type: WS_READY,
        payload: true,
      });
      dispatch({
        type: GLOBAL_LOADING,
        payload: false,
      });
    } else if (config.isMockUp && websocket.ready === null) {
      // ***MOCK-UP MODE
      // const jwt = window.localStorage.getItem('winamax-jwt');
      const jwt = getCookie(config.nameCookieJWT);
      if (jwt) {
        dispatch({
          type: SAVE_JWT,
          payload: jwt,
        });
      }
      dispatch({
        type: WS_READY,
        payload: false,
      });

      dispatch({
        type: WS_GET_USER_REGISTRATION_REQUEST,
        payload: {
          method: API.GetUserRegistration,
          params: { jwt: jwt || '' },
          backgroundAPI: true,
        },
      });
    }
  }, [
    common.jwt,
    common.jwtExpirationTime,
    dispatch,
    history,
    redirectUser,
    userRegistration.getUserRegistration.error,
    userRegistration.getUserRegistration.loading,
    userRegistration.getUserRegistration.success,
    userRegistration.initialized,
    userRegistration.initializing,
    websocket.ready,
    websocket.layerError,
    session.setSessionId.success,
  ]);

  // Change language
  useEffect(() => {
    if (history.location.search) {
      const lang = getParameterByName('lang', history.location.search);
      if (lang) {
        changeLanguageL10n(lang);
      }
      if (!config.isProd) {
        // Debug mode
        if (getParameterByName('debug', history.location.search)) config.isDebug = true;
        // Mockup mode
        if (getParameterByName('mockup', history.location.search)) config.isMockUp = true;
        // Force routes
        if (getParameterByName('force', history.location.search)) config.isForce = true;
      }
    } else {
      const lang = localStorage.getItem('wmx-lang');
      if (lang) {
        changeLanguageL10n(lang);
      }
    }
    setGetParamsInitialized(true);
    dispatch({
      type: SET_LANGUAGE,
      payload: getLang(),
    });
  }, [dispatch, history.location.search]);

  const checkTimeout = useCallback(() => {
    if (websocket.layerError) return;

    const toWatch = { ...userRegistration, ...deposit };
    const currentTime = Date.now();
    Object.entries(toWatch).forEach((entry) => {
      if (
        entry
        && entry.length === 2
        && entry[1]
        && typeof entry[1] === 'object'
        && entry[1].time
        && currentTime - parseInt(entry[1].time, 10) > config.requestTimeout
      ) {
        dispatch({ type: WS_CLEAR_PENDING });
        dispatch({ type: WS_CLEAR_RESPONSES });
        dispatch({
          type: GLOBAL_LOADING,
          payload: false,
        });
        dispatch({
          type: WS_DISPLAY_ERROR_LAYER,
          payload: {
            message: __('The current process seems to be slow. Try again.'),
          },
        });
      }
    });
  }, [deposit, dispatch, userRegistration, websocket.layerError]);

  useEffect(() => {
    timeoutInterval.current = setInterval(checkTimeout, 1000);
    return () => {
      clearInterval(timeoutInterval.current);
    };
  }, [checkTimeout]);

  // Get Session
  useEffect(() => {
    if (common.jwt && session.isFetching) {
      dispatch({ type: GET_SESSION_ID });
    }
  }, [common.jwt, session.isFetching]);

  const onCaptchaLoad = useCallback(async () => {
    setCaptchaLoaded(true);
  }, [dispatch]);

  useEffect(() => {
    if (captchaLoaded && userRegistration.captcha.fetching) {
      if (!config.hosted) {
        setCaptchaToken('GO0DT0K3N');
      } else if (captchaRef.current) {
        captchaRef.current.execute();
      }
    }
  }, [captchaLoaded, userRegistration.captcha.fetching]);

  useEffect(() => {
    if (captchaToken) {
      dispatch({
        type: SET_CAPTCHA,
        payload: captchaToken,
      });
    }
  }, [captchaToken, dispatch]);

  return (
    <MainStyle
      key={language}
      className={className}
      $scrolled={scrolled}
      $noHeader={noHeader}
    >
      {config.shouldUseHcaptcha && (
        <HCaptcha
          ref={captchaRef}
          sitekey={config.captchaKey}
          size={'invisible'}
          onLoad={onCaptchaLoad}
          onVerify={setCaptchaToken}
          sentry={false}
        />
      )}
      <DocEltContextProvider>{children}</DocEltContextProvider>
    </MainStyle>
  );
};
